import { useEffect, useState } from "react";
import { Sparkles, ChevronDown } from "lucide-react";
import { api, formatApiErrorDetail } from "../../lib/api";
import { toast } from "../ui/sonner";
import { Button } from "../ui/button";

export const AdminMatchCandidates = ({ sessionId, onCreateMatch }) => {
  const [candidates, setCandidates] = useState(null);
  const [showExcluded, setShowExcluded] = useState(false);

  const load = () => {
    api.get(`/admin/sessions/${sessionId}/match-candidates`).then(({ data }) => setCandidates(data));
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sessionId]);

  const accept = async (c) => {
    try {
      await onCreateMatch(c.booking_id_a, c.booking_id_b);
      load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    }
  };

  if (candidates === null) return null;
  const compatible = candidates.filter((c) => c.compatible);
  const excluded = candidates.filter((c) => !c.compatible);

  return (
    <div className="mb-8" data-testid="admin-match-candidates-panel">
      <h3 className="font-extrabold uppercase text-sm tracking-wide mb-3 flex items-center gap-2">
        <Sparkles className="w-4 h-4 text-stranger-orange" /> Suggested Pairs
      </h3>
      {compatible.length === 0 ? (
        <p className="text-sm font-medium text-midnight-black/50" data-testid="no-match-candidates">
          No compatible pairs to suggest right now — register more Strangers or match manually below.
        </p>
      ) : (
        <div className="space-y-2" data-testid="match-candidates-list">
          {compatible.map((c, idx) => (
            <div key={`${c.booking_id_a}-${c.booking_id_b}`} className="border-2 border-soft-gray rounded-xl p-4 flex items-center justify-between gap-4 flex-wrap" data-testid={`match-candidate-${idx}`}>
              <div>
                <p className="font-extrabold text-sm uppercase">
                  {c.stranger_a_name} + {c.stranger_b_name}
                  <span className="ml-2 text-[10px] font-bold tracking-widest uppercase px-2 py-0.5 rounded-full bg-stranger-orange text-clean-white" data-testid={`match-candidate-score-${idx}`}>{c.score}% match</span>
                </p>
                <ul className="text-xs font-medium text-midnight-black/60 mt-1 list-disc list-inside">
                  {c.reasons.map((r, i) => (
                    <li key={i}>{r}</li>
                  ))}
                </ul>
              </div>
              <Button data-testid={`accept-match-candidate-${idx}`} onClick={() => accept(c)} className="bg-midnight-black hover:bg-midnight-black/80 text-clean-white font-bold rounded-full text-xs">
                CREATE THIS MATCH
              </Button>
            </div>
          ))}
        </div>
      )}

      {excluded.length > 0 && (
        <div className="mt-4">
          <button
            data-testid="toggle-excluded-pairs"
            onClick={() => setShowExcluded((v) => !v)}
            className="flex items-center gap-1.5 text-xs font-bold uppercase tracking-wide text-midnight-black/50 hover:text-midnight-black"
          >
            <ChevronDown className={`w-3.5 h-3.5 transition-transform ${showExcluded ? "rotate-180" : ""}`} />
            {showExcluded ? "Hide" : "Show"} excluded pairs ({excluded.length})
          </button>
          {showExcluded && (
            <div className="space-y-2 mt-3" data-testid="excluded-pairs-list">
              {excluded.map((c, idx) => (
                <div key={`${c.booking_id_a}-${c.booking_id_b}`} className="border border-dashed border-soft-gray rounded-xl p-4 bg-soft-gray/20" data-testid={`excluded-pair-${idx}`}>
                  <p className="font-bold text-sm">{c.stranger_a_name} + {c.stranger_b_name}</p>
                  <p className="text-xs font-medium text-midnight-black/50 mt-1">{c.exclusion_reason}</p>
                </div>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
};
